import { isNetworkFailure, NETWORK_FAILURE_MESSAGE } from '../utils/networkFailure';

const CHOICE_DELIMITER = '::';
// [\s\S]: the body may hold a line separator, e.g. from a user's cell value.
const CLOUD_RUN_ERROR_PATTERN = /Cloud Run call failed:\s*(\d+)\s*-\s*([\s\S]*)$/;
const STALE_OR_INACTIVE_IDENTIFIER_CODE = 'stale_or_inactive_identifier';
export const REASON_COUNTRY_HINT_NOT_AVAILABLE = 'country_hint_not_available';

export const ISSUE_COUNTRY_CHOICE_REQUIRED = 'country_choice_required';
export const ISSUE_LEGACY_IDENTIFIER_INFO = 'legacy_identifier_info';
export const ISSUE_RUNTIME_UNRESOLVED = 'runtime_unresolved';
export const ISSUE_AUTO_RESOLVED_US_INFO = 'auto_resolved_us_info';
export const ISSUE_COUNTRY_CHOICE_SAVED = 'country_choice_saved';
export const ISSUE_COUNTRY_CURRENT = 'country_current';
export const ISSUE_COUNTRY_HINT_FALLBACK_INFO = 'country_hint_fallback_info';
export const ISSUE_MARKET_REGISTRATION_PENDING = 'market_registration_pending';
export const ISSUE_MARKET_REGISTRATION_FAILED = 'market_registration_failed';

export const APP_READINESS_READY = 'ready';
export const APP_READINESS_WAITING_MARKET_REGISTRATION = 'waiting_market_registration';
export const APP_READINESS_MARKET_REGISTRATION_FAILED = 'market_registration_failed';
export const APP_READINESS_IDENTITY_UNRESOLVED = 'identity_unresolved';

export const SELECTION_MODE_SAVED = 'saved';
export const SELECTION_MODE_DEFAULT_US = 'default_us';

export const COUNTRY_CHOICE_ROW_ISSUES = new Set([
    ISSUE_COUNTRY_CHOICE_REQUIRED,
    ISSUE_AUTO_RESOLVED_US_INFO,
    ISSUE_COUNTRY_CHOICE_SAVED,
    ISSUE_COUNTRY_CURRENT,
]);

export const NOTE_ONLY_ROW_ISSUES = new Set([
    ISSUE_LEGACY_IDENTIFIER_INFO,
    ISSUE_RUNTIME_UNRESOLVED,
    ISSUE_COUNTRY_HINT_FALLBACK_INFO,
    ISSUE_MARKET_REGISTRATION_PENDING,
    ISSUE_MARKET_REGISTRATION_FAILED,
]);

// Notes render for every review item that carries a user-facing message,
// including action-required items like country_choice_required — the chips
// alone don't tell the user *why* a choice is needed.
const NOTE_MESSAGE_ISSUES = new Set([
    ...NOTE_ONLY_ROW_ISSUES,
    ISSUE_COUNTRY_CHOICE_REQUIRED,
    ISSUE_COUNTRY_CURRENT,
]);

const normalizeString = (value) => {
    if (value === undefined || value === null) {
        return '';
    }
    return String(value).trim();
};

export function buildMsiChoiceValue(option) {
    const country = normalizeString(option?.country);
    const canonicalCogniveId = normalizeString(option?.canonical_cognive_id);
    if (!country || !canonicalCogniveId) {
        return '';
    }
    return `${country}${CHOICE_DELIMITER}${canonicalCogniveId}`;
}

export function parseMsiChoiceValue(rawValue) {
    const value = normalizeString(rawValue);
    if (!value) {
        return null;
    }

    const [selectedCountry, selectedCanonicalCogniveId] = value.split(CHOICE_DELIMITER);
    const normalizedCountry = normalizeString(selectedCountry);
    const normalizedCanonicalCogniveId = normalizeString(selectedCanonicalCogniveId);

    if (!normalizedCountry || !normalizedCanonicalCogniveId) {
        return null;
    }

    return {
        selected_country: normalizedCountry,
        selected_canonical_cognive_id: normalizedCanonicalCogniveId,
    };
}

export function deriveSavedChoiceValue(savedChoice, currentOptions) {
    if (!savedChoice || !Array.isArray(currentOptions) || currentOptions.length === 0) {
        return '';
    }

    const targetCountry = normalizeString(savedChoice?.selected_country);
    const targetCanonical = normalizeString(savedChoice?.selected_canonical_cognive_id);
    if (!targetCountry || !targetCanonical) {
        return '';
    }

    const matchedOption = currentOptions.find((option) => {
        const optionCountry = normalizeString(option?.country);
        const optionCanonical = normalizeString(option?.canonical_cognive_id);
        return optionCountry === targetCountry && optionCanonical === targetCanonical;
    });

    return buildMsiChoiceValue(matchedOption);
}

export function deriveSelectedOptionValues(currentOptions) {
    if (!Array.isArray(currentOptions) || currentOptions.length === 0) {
        return [];
    }
    const values = [];
    const seen = new Set();
    for (const option of currentOptions) {
        if (!option || option.is_selected !== true) {
            continue;
        }
        const value = buildMsiChoiceValue(option);
        if (!value || seen.has(value)) {
            continue;
        }
        seen.add(value);
        values.push(value);
    }
    return values;
}

export function parseCloudRunError(errorLike) {
    if (errorLike?.name === 'BackendHttpError' && Number.isFinite(errorLike.status)) {
        return {
            rawMessage: errorLike.message, message: errorLike.message,
            statusCode: errorLike.status, code: errorLike.code, detail: errorLike.detail,
            reference: errorLike.reference, retryable: errorLike.retryable,
        };
    }
    const rawMessage = errorLike instanceof Error
        ? errorLike.message
        : normalizeString(errorLike);

    const match = rawMessage.match(CLOUD_RUN_ERROR_PATTERN);
    if (!match) {
        return {
            rawMessage,
            statusCode: null,
            code: null,
            detail: null,
            message: isNetworkFailure(errorLike)
                ? NETWORK_FAILURE_MESSAGE
                : rawMessage || 'Request failed.',
        };
    }

    const statusCode = Number(match[1]);
    const payloadText = normalizeString(match[2]);

    let parsedPayload = null;
    try {
        parsedPayload = payloadText ? JSON.parse(payloadText) : null;
    } catch (error) {
        parsedPayload = null;
    }

    const detail = parsedPayload && typeof parsedPayload === 'object' && 'detail' in parsedPayload
        ? parsedPayload.detail
        : parsedPayload;

    const code = detail && typeof detail === 'object' ? normalizeString(detail.code) : '';
    const detailMessage = detail && typeof detail === 'object'
        ? normalizeString(detail.message || detail.detail)
        : normalizeString(detail);

    return {
        rawMessage,
        statusCode: Number.isFinite(statusCode) ? statusCode : null,
        code: code || null,
        detail,
        message: detailMessage || rawMessage || 'Request failed.',
    };
}

export function isWatchlistStaleImportApplyError(parsedError) {
    return (
        [
            'portfolio_import_apply_stale',
            'watchlist_editor_apply_stale',
        ].includes(parsedError?.code)
        && normalizeString(parsedError?.detail?.stale_context).toLowerCase() === 'watchlist'
    );
}

export function isPreviewStaleImportApplyError(parsedError) {
    return (
        [
            'portfolio_import_apply_stale',
            'watchlist_editor_apply_stale',
        ].includes(parsedError?.code)
        && normalizeString(parsedError?.detail?.stale_context).toLowerCase() === 'preview'
    );
}

export function portfolioImportReviewVersionKey(run) {
    const runId = normalizeString(run?.id);
    if (!runId) return '';
    return JSON.stringify([
        runId,
        normalizeString(run?.source_metadata_json?.staged_state_hash),
        normalizeString(run?.base_state_hash),
        normalizeString(run?.base_watchlist_revision_id),
        normalizeString(run?.base_watchlist_state_hash),
    ]);
}

export function buildLegacyIdentifierNote(item) {
    const uploaded = item?.uploaded_identifier || {};
    const uploadedType = String(uploaded?.id_type || '').toUpperCase();
    const uploadedValue = String(uploaded?.id_value || '').trim();

    if (uploadedType && uploadedValue) {
        return `System auto-resolved uploaded ${uploadedType} ${uploadedValue} from an older identifier to this security. Please refresh this identifier in future uploads to keep data current.`;
    }
    return item?.message
        || 'System auto-resolved an older uploaded identifier to this security. Please refresh this identifier in future uploads to keep data current.';
}

export function buildRuntimeUnresolvedNote(item) {
    if (String(item?.reason_code || '').trim().toLowerCase() === STALE_OR_INACTIVE_IDENTIFIER_CODE) {
        const uploaded = item?.uploaded_identifier || {};
        const uploadedType = String(uploaded?.id_type || '').trim().toUpperCase();
        const uploadedValue = String(uploaded?.id_value || '').trim();
        const subject = uploadedType && uploadedValue
            ? `Uploaded ${uploadedType} ${uploadedValue}`
            : 'This watchlist security';
        return `${subject} is not active in the current security master, so it was not included in your watchlist. It may be stale, delisted, or replaced. Remove or update this security in your watchlist and re-upload.`;
    }

    const message = normalizeString(item?.message);
    const nextStep = normalizeString(item?.next_step);
    if (message && nextStep && !message.includes(nextStep)) {
        return `${message} ${nextStep}`;
    }
    if (message) {
        return message;
    }
    if (nextStep) {
        return `System could not resolve this uploaded identifier to a supported security. ${nextStep}`;
    }
    return 'System could not resolve this uploaded identifier to a supported security.';
}

export function buildReviewNote(item) {
    if (!item) {
        return '';
    }
    const issueType = item.issue_type;
    if (issueType === ISSUE_LEGACY_IDENTIFIER_INFO) {
        return buildLegacyIdentifierNote(item);
    }
    if (issueType === ISSUE_RUNTIME_UNRESOLVED) {
        return buildRuntimeUnresolvedNote(item);
    }
    return formatUserFacingReviewMessage(item.message);
}

export function formatUserFacingReviewMessage(message) {
    return normalizeString(message)
        .replace(/\buploaded[ _-]+country[ _-]+hint\b/gi, 'requested country')
        .replace(/\bcountry[ _-]+hint[ _-]+not[ _-]+available\b/gi, 'country not available')
        .replace(/\buploaded[ _-]+country\b/gi, 'requested country')
        .replace(/\bcountry[ _-]+hint\b/gi, 'requested country');
}

function buildDefaultChoiceValue(defaultSelectedChoice) {
    if (!defaultSelectedChoice) {
        return '';
    }
    return buildMsiChoiceValue({
        country: defaultSelectedChoice.selected_country,
        canonical_cognive_id: defaultSelectedChoice.selected_canonical_cognive_id,
    });
}

export function resolveInitialSelectionValueForItem(item) {
    const currentOptions = Array.isArray(item?.current_options) ? item.current_options : [];
    if (item?.issue_type === ISSUE_COUNTRY_CURRENT) {
        return currentOptions.length > 0
            ? buildMsiChoiceValue(currentOptions[0])
            : '';
    }
    const selectionMode = item?.selection_mode || '';
    if (selectionMode === SELECTION_MODE_SAVED) {
        return deriveSavedChoiceValue(item?.saved_choice, currentOptions);
    }
    if (selectionMode === SELECTION_MODE_DEFAULT_US && item?.default_selected_choice) {
        return buildDefaultChoiceValue(item.default_selected_choice);
    }
    return deriveSavedChoiceValue(item?.saved_choice, currentOptions);
}

export function getRowCountryChoiceItem(row) {
    const items = Array.isArray(row?.review_items) ? row.review_items : [];
    return items.find((item) => COUNTRY_CHOICE_ROW_ISSUES.has(item?.issue_type)) || null;
}

export function isCountryRepairReviewItem(item) {
    if (!item || item.issue_type !== ISSUE_COUNTRY_CHOICE_REQUIRED) {
        return false;
    }
    if (item.is_repair === true) {
        return true;
    }
    return String(item.reason_code || '').trim().toLowerCase() === REASON_COUNTRY_HINT_NOT_AVAILABLE;
}

export function getRowCountryRepairItem(row) {
    const item = getRowCountryChoiceItem(row);
    return isCountryRepairReviewItem(item) ? item : null;
}

export function buildCountryRepairPayloadFromRow(row, selectedValue) {
    const item = getRowCountryRepairItem(row);
    if (!item) {
        return null;
    }
    const parsed = parseMsiChoiceValue(selectedValue);
    if (!parsed) {
        return null;
    }
    return {
        id_type: item.id_type,
        id_value_norm: item.id_value_norm,
        selected_country: parsed.selected_country,
        selected_canonical_cognive_id: parsed.selected_canonical_cognive_id,
    };
}

export function getRowNoteItems(row) {
    const items = Array.isArray(row?.review_items) ? row.review_items : [];
    return items.filter((item) => NOTE_MESSAGE_ISSUES.has(item?.issue_type));
}

export function deriveRowNotes(row) {
    const seen = new Set();
    const notes = [];
    for (const item of getRowNoteItems(row)) {
        const note = buildReviewNote(item);
        if (!note || seen.has(note)) {
            continue;
        }
        seen.add(note);
        notes.push(note);
    }
    return notes;
}
